import { Dropdown, IDropdownOption, Spinner, SpinnerSize, Stack, StackItem } from '@fluentui/react';
import { observer } from 'mobx-react';
import { ProvisionProfileEditorState } from './ProvisionProfileEditor';

import { InfoTooltipLabel } from '../../controls/InfoTooltipLabel';
import { ProvisionType } from '../../../API';
import ExpressionField from '../expression-builder/ExpressionField';
import TokenPathTagPicker, { TokenPathTagPickerState } from '../../controls/TokenPathTagPicker';

function ProfileWorkspaceDetails({ state }: { state: ProvisionProfileEditorState }) {
	const provisionTypeOptions: IDropdownOption[] = [
		{
			key: ProvisionType.NoWorkspace,
			text: 'Don\'t create a workspace',
		},
		{
			key: ProvisionType.Site,
			text: 'SharePoint Site',
		},
		{
			key: ProvisionType.Library,
			text: 'Document Library',
		},
		{
			key: ProvisionType.Folder,
			text: 'Folder',
		},
		{
			key: ProvisionType.Team,
			text: 'Microsoft Team',
		},
		{
			key: ProvisionType.TeamChannel,
			text: 'Microsoft Team Channel',
		}
	];
	

	return (<>
			<Stack>

					<Stack horizontal>
						<StackItem>
							
							<InfoTooltipLabel label='Type' title='Select the type of workspace for this profile.'>

								<Dropdown
									style={{width:350}}
									options={provisionTypeOptions}
									selectedKey={state.ProvisionType}
									onChange={(_, o) => { state.setProvisionType(o?.key as ProvisionType); }}
								/>
							</InfoTooltipLabel>
						</StackItem>

						<StackItem>
							{ state.ProvisionType === ProvisionType.Site && <>
							
								<div style={{marginLeft:10}}>
									<InfoTooltipLabel 
												label={`Base site template`}
												title={`Select a default SharePoint site template to use when creating new sites. Note that not all templates will work successfully - simple Team Sites or Communication Sites are recommended.`}>

											{state.availableSiteTemplates ? <>
												<Dropdown
													style={{width:350}}
													options={(state.availableSiteTemplates || []).map(template => ({
														key: template.name,
														text: `${template.title} (${template.name})`
													}))}
													selectedKey={state.SiteTemplate}
													onChange={(_, o) => { state.setSiteTemplate(o?.key as string); }}
												/>
											</> : <Spinner size={SpinnerSize.medium} /> }
									</InfoTooltipLabel>
								</div>
							</>}
						</StackItem>

					</Stack>

				{state.ProvisionType !== ProvisionType.NoWorkspace && <>
					
					<Stack horizontal grow>
						<StackItem grow>
							<InfoTooltipLabel 
											label={`Title`}
											title={`Enter the title of the SharePoint workspace. Construct your workspace title (e.g. Site, Team, Library etc) from these tokens and the it will be generated dynamically based on your metadata.`}>

									<TokenPathTagPicker
										state={new TokenPathTagPickerState(state.setWorkspaceTitle.bind(state), '', undefined, state.WorkspaceTitle, state.tokens ?? [])}
									/>
							</InfoTooltipLabel>
						</StackItem>

						{state.ProvisionType === ProvisionType.Team && <>
							<StackItem grow style={{ marginLeft: 10 }}>

								<InfoTooltipLabel 
									label={`Mail Nickname (optional)`}
									title={`Optionally specify a custom mail nickname for the Microsoft Team. If not provided, it will be automatically generated from the team name. You can use tokens to construct it dynamically.`}>

									<TokenPathTagPicker
										state={new TokenPathTagPickerState(state.setMailNickname.bind(state), '', undefined, state.MailNickname, state.tokens ?? [])}
									/>
								</InfoTooltipLabel>
							</StackItem>

							<StackItem grow style={{ marginLeft: 10 }}>

								<InfoTooltipLabel 
									label={`Description (optional)`}
									title={`Optionally specify a custom description for the Microsoft Team.`}>

									<TokenPathTagPicker
										state={new TokenPathTagPickerState(state.setDescription.bind(state), '', undefined, state.Description, state.tokens ?? [])}
									/>
								</InfoTooltipLabel>
							</StackItem>
						</>}
					</Stack>



					{state.ProvisionType === ProvisionType.Library || state.ProvisionType === ProvisionType.Folder || state.ProvisionType === ProvisionType.TeamChannel ? 
						
						<InfoTooltipLabel 
							label={`Parent ${state.getParentTypeName()} to create new ${state.entityTypePluralName} under`}
							title='Construct your URL from these tokens and the URL will be generated dynamically based on your metadata. Note that if you reference a lookup to a parent list, ensure the lookup is indexed.'>

							<TokenPathTagPicker
								state={new TokenPathTagPickerState(state.setWorkspacePath.bind(state), '', undefined, state.WorkspacePath, state.tokens ?? [])}
							/>

						</InfoTooltipLabel>

						: <></> 
					}
					



					<div>

						{ (state.ProvisionType === ProvisionType.Site || state.ProvisionType === ProvisionType.Library) &&
						<>
						
							
							<InfoTooltipLabel 
								label={`URL ending for this ${ProvisionType[state.ProvisionType].toLowerCase()}`}
								title='Construct your URL ending from these tokens and the URL will be generated dynamically based on your metadata.'>

								<TokenPathTagPicker
									state={new TokenPathTagPickerState(state.setWorkspacePathComponent.bind(state), '', state.ProvisionType === ProvisionType.Site ? (state.userConfig.tenantUrl + 'sites/') : undefined, state.WorkspacePathComponent, state.tokens ?? [])}
								/>
							</InfoTooltipLabel>
							
						</> 
						}
					</div>

					
					<InfoTooltipLabel 
						label={`Create ${ProvisionType[state.ProvisionType].toLowerCase()} when`}
						title='Enter an expression to determine whether provisioning of a workspace can take place.'>
						
						<ExpressionField state={state.createWorkspaceExpressionState} />
					</InfoTooltipLabel>

				</>}
			</Stack>
		</>
	);
}

export default observer(ProfileWorkspaceDetails);
